
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Button } from "primereact/button";
import ApiResponse from "../../Models/ApiResponse";
import { resetPasswordRetrieveService, resetPasswordService } from "../../Services/authenticationService";
import SecurityUserResponse from "../../Models/Authentication/SecurityUserResponse";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DashlyPasswordInput } from "../../Components/inputs/DashlyPasswordInput/DashlyPasswordInput";
import "./signIn.scss"

interface ResetPasswordFormPropsComponent {
    uuid: string | undefined;
    setSuccessMessage: Dispatch<SetStateAction<string | null>>;
    setErrorMessage: Dispatch<SetStateAction<string | null>>;
    onBackToLogin: (username?: string, password?: string) => void;
}

interface ResetPasswordFormProps {
    password: string;
    confirmPassword: string;
}

const ResetPasswordForm = ({
    uuid,
    setSuccessMessage,
    setErrorMessage,
    onBackToLogin
}: ResetPasswordFormPropsComponent) => {
    const { handleSubmit, control } = useForm<ResetPasswordFormProps>();
    const [securityUserResponse, setSecurityUserResponse] = useState<SecurityUserResponse | null>(null);
    const [message, setMessage] = useState("");
    const location = useLocation();
    const navigate = useNavigate();
    const [hash, setHash] = useState(uuid);
    const [processed, setProcessed] = useState(false);
    const { t } = useTranslation();
    const [type, setType] = useState("")
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!processed && hash) {
            const hashType = hash.charAt(0);
            const trimmedHash = hash.substring(1);

            setType(hashType)
            switch (hashType) {
                case 'R':
                    setMessage(t("authentication.password-reset"));
                    setHash(trimmedHash);
                    break;
                case 'N':
                    setMessage(t("authentication.password-new"));
                    setHash(trimmedHash);
                    break;
                case 'E':
                    setMessage(t("authentication.password-expired"));
                    setHash(trimmedHash);
                    break;
                default:
                    setProcessed(true);
                    break;
            }

            if (['R', 'N', 'E'].includes(hashType)) {
                setProcessed(true);
            }
        }
    }, [hash, processed]);

    useEffect(() => {
        if (processed) {
            fetchData();
        }
    }, [processed]);

    const fetchData = () => {
        if (hash && hash.length === 36) {
            resetPasswordRetrieveService(hash ?? '')
                .then(response => {
                    let apiResponse = response?.data as ApiResponse<SecurityUserResponse>;
                    setSecurityUserResponse(apiResponse.data);
                })
                .catch(() => {
                    setErrorMessage(t("login.message.password.reset.retrieval.failed"));
                    onBackToLogin();
                    window.history.replaceState(null, "", "/sign-in")
                });
        }
    }

    const handleBack = () => {
        onBackToLogin();
        navigate("/sign-in", { replace: true });
    };

    const onSubmit = handleSubmit((data) => {
        if (submitting) {
            return;
        }
        if (!hash) {
            setErrorMessage(t("login.message.password.reset.invalid.token"));
            return;
        }
        if (data.password !== data.confirmPassword) {
            setErrorMessage(t("login.message.password.reset.no.match"));
            return;
        }
        setSubmitting(true);
        resetPasswordService(hash, data.password)
            .then(() => {
                setSuccessMessage(t("login.message.password.reset"));
                onBackToLogin(securityUserResponse?.username ?? '', data.password);
            })
            .catch((axiosError) => {
                const apiResponse = axiosError?.response?.data as ApiResponse<string> | undefined;
                const apiMessage = apiResponse?.message;
                setErrorMessage(typeof apiMessage === "string" && apiMessage.trim() !== ""
                    ? apiMessage
                    : t("common.error"));
            })
            .finally(() => {
                setSubmitting(false);
            });
    });

    return (
        securityUserResponse && (
            <form className="sign-in__form" onSubmit={onSubmit}>
                <div className="sign-in__intro">
                    <h1 className="sign-in__title">{t("login.reset-password")}</h1>
                    <p className="sign-in__subtitle">{message}</p>
                </div>

                <div className="sign-in__fields">
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <DashlyPasswordInput
                                {...field}
                                placeholder={t("login.message.password.reset.new.password")}
                                toggleMask
                                feedback={false}
                                autoComplete="new-password"
                                autoFocus
                                required
                            />
                        )}
                    />

                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field }) => (
                            <DashlyPasswordInput
                                {...field}
                                placeholder={t("login.message.password.reset.confirm.password")}
                                toggleMask
                                feedback={false}
                                autoComplete="new-password"
                                required
                            />
                        )}
                    />
                </div>

                <div className="sign-in__actions">
                    <Button
                        text
                        className="sign-in__action-btn sign-in__action-btn--cancel"
                        type="button"
                        label={t("common.action.back")}
                        onClick={handleBack}
                    />
                    <Button
                        className="sign-in__action-btn sign-in__action-btn--primary"
                        type="submit"
                        label={type === "N" ? t("common.save") : t("login.reset-password")}
                        disabled={submitting}
                        loading={submitting}
                    />
                </div>
            </form>
        )
    );
};

export default ResetPasswordForm;
